/**
 * Communities Types
 * Type definitions for Communities Management feature
 */

export interface CommunityProfile {
  profileId: number;
  appId: number;
  accountId: number;
  actorId: number; // The location/site this community belongs to
  profileName: string;
  profileDescription: string;
  usageType: number | string; // MachineUsage enum: 1=Public, 2=SemiPublic, 3=PrivateHouse, 4=SharedBuilding
  profileStatus: string; // "Deleted" | "Active" (API returns enum as string)
  processOrder: number;
  strategyType: number | string; // API can return number (1,2,3) or string enum ("Percentage", "FixedAmount", "ByKwh", "FixedPercentage")
  singleDimensionValue: number | null;
  strategySettings: string;
  startDate: string; // ISO date
  expirationDate: string | null;
  lastUpdated: string;
  updatedByName: string;
  assignedAccounts?: number; // Number of accounts currently assigned to this community (non-expired)
}

export interface GetCommunitiesResponse {
  communities: CommunityProfile[];
  success: boolean;
  message: string | null;
}

export interface GetCommunityResponse {
  profile: CommunityProfile;
}

export interface InsertCommunityRequest {
  actorId: number;
  profileName: string;
  profileDescription: string;
  usageType?: number; // MachineUsage enum - defaults to 0 if not provided
  profileStatus: number; // 1=Deleted, 0=Active (API expects numeric enum) - REQUIRED
  strategyType: number;
  singleDimensionValue?: number | null;
  strategySettings?: string;
  startDate: string;
  expirationDate?: string | null;
  processOrder: number;
  updatedByName: string;
}

export interface InsertCommunityResponse {
  completed: boolean;
  profileId: number;
  success: boolean;
  message: string | null;
}

export interface UpdateCommunityRequest {
  profileId: number;
  actorId?: number; // Allow updating the location
  profileName?: string;
  profileDescription?: string;
  usageType?: number; // MachineUsage enum: 1=Public, 2=SemiPublic, 3=PrivateHouse, 4=SharedBuilding
  profileStatus?: number; // 0=Deleted, 1=Active (API expects numeric enum)
  processOrder?: number;
  strategyType?: number;
  singleDimensionValue?: number | null;
  strategySettings?: string;
  startDate?: string;
  expirationDate?: string | null;
  updatedByName?: string;
}

export interface UpdateCommunityResponse {
  community: CommunityProfile | null;
  success: boolean;
  message: string | null;
}

// UI-specific types
export interface CommunitiesFilters {
  includeExpired: boolean;
  actorId: number | null;
  page: number; // Current page (1-indexed)
  pageSize: number; // Items per page
}

// Enums for clarity
export enum ProfileStatus {
  Deleted = 1,  // Soft-deleted
  Active = 0
}

export enum MachineUsage {
  Public = 1,
  SemiPublic = 2,
  PrivateHouse = 3,
  SharedBuilding = 4
}

export enum StrategyType {
  FixedPercentageEnergy = 0,    // Discount only on energy (percentage) - Backend value is 0
  FixedCentsPerKwh = 1,         // Discount on energy (cents per kWh) - Backend value is 1
  FixedPercentage = 2           // Discount from whole session (percentage) - Backend value is 2
}

export interface UpsertCommunityAccountRelationRequest {
  profileId: number;
  accountId: number;
  startDate: string; // ISO date string (YYYY-MM-DD)
  expirationDate?: string | null; // ISO date string, optional
  updatedBy?: number | null;
  updatedByName?: string;
}

export interface UpsertCommunityAccountRelationResponse {
  accountProfileId: number;
  completed: boolean;
  success: boolean;
  message: string | null;
}
